import { supabase } from '../auth/supabase'

/**
 * Inviting somebody into the household, and finding out where that got to.
 *
 * Everything here needs the network, and that is not a compromise of the
 * offline rule — it is the point of the separation. *Adding* a member is a
 * store action and works on a train (`addMember`); *inviting* them creates an
 * account on a server and sends mail, which cannot be faked locally and must
 * not be attempted silently. So the two are different buttons, and this one is
 * explicit, retryable, and says what happened.
 *
 * The invitation itself carries no secret this app invented. It is Supabase's
 * own auth token, delivered by the auth mailer over the project's SMTP — the
 * same code, the same template family and the same expiry as signing in, which
 * is the whole reason there is no `code_hash` column anywhere in this schema.
 */

/** What the *Foyer* list needs to say about one member, beyond their name. */
export interface MemberStatus {
  /** The member has an account and has signed in with it at least once. */
  hasAccount: boolean
  /** ISO timestamp of the last invitation sent, or null if none ever was. */
  invitedAt: string | null
}

/**
 * The parts of a member's state that are deliberately not synced.
 *
 * `user_id` never crosses the wire (`src/sync/schema.ts`), and `member_invites`
 * is outside the sync engine because a server-side write to a synced row beats a
 * genuine offline edit at last-write-wins. Both are still worth *showing*, so
 * this reads them directly, on the one screen that asks.
 *
 * Returns an empty map rather than throwing when there is no network: a Foyer
 * list that shows names and no badges is right, and one that refuses to render
 * is not.
 */
export async function readMemberStatus(householdId: string): Promise<Map<string, MemberStatus>> {
  const status = new Map<string, MemberStatus>()
  const client = supabase
  if (!client || !householdId) return status

  const [membersResult, invitesResult] = await Promise.all([
    client.from('household_members').select('id,user_id').eq('household_id', householdId),
    // Readable by the owner alone. Everybody else gets an empty list, which is
    // the correct amount of information for somebody who cannot send one.
    client
      .from('member_invites')
      .select('member_id,invited_at')
      .eq('household_id', householdId),
  ])

  if (membersResult.error) throw membersResult.error

  for (const row of membersResult.data ?? []) {
    status.set(String(row.id), { hasAccount: row.user_id !== null, invitedAt: null })
  }

  for (const row of invitesResult.data ?? []) {
    const existing = status.get(String(row.member_id))
    if (existing) existing.invitedAt = String(row.invited_at)
  }

  return status
}

/** What the Edge Function answers with, turned into something a person reads. */
const MESSAGES: Record<string, string> = {
  not_owner: 'Seul le propriétaire du foyer peut envoyer une invitation.',
  member_not_found: 'Ce membre n’existe plus dans le foyer.',
  no_auth_email: 'Ce membre n’a pas d’adresse e-mail : modifie-le d’abord.',
  invalid_email: 'L’adresse e-mail de ce membre n’est pas valide.',
  already_claimed: 'Ce membre a déjà un compte : aucune invitation nécessaire.',
  email_taken: 'Cette adresse est déjà utilisée par un autre compte.',
  rate_limited: 'Trop d’envois : réessaie dans quelques minutes.',
}

/**
 * The body of a failed function call, which is where the reason actually lives.
 *
 * `functions.invoke` reports a non-2xx as a `FunctionsHttpError` whose own
 * message is the useless "Edge Function returned a non-2xx status code"; the
 * response it carries is the only place the code appears. Swallowing that is
 * exactly the shape of failure `formatSyncError` exists to stop repeating.
 */
async function reasonFrom(error: unknown): Promise<string> {
  const response = (error as { context?: unknown })?.context
  if (response instanceof Response) {
    try {
      const body = (await response.clone().json()) as { error?: unknown }
      const code = typeof body.error === 'string' ? body.error : ''
      if (code) return MESSAGES[code] ?? code
    } catch {
      // A function that crashed before answering JSON. Fall through.
    }
  }
  return error instanceof Error ? error.message : String(error)
}

/**
 * Send (or re-send) the invitation for one member.
 *
 * Only the member's *id* is sent. The address, the username and the household
 * are all read server-side from the row, so a client cannot invite an address
 * the owner never typed — and the function checks that the caller owns the
 * household through RLS before it touches anything privileged.
 *
 * Throws with a French message on failure, because the one caller is a button
 * whose entire job is to report what happened.
 */
export async function sendInvitation(memberId: string): Promise<void> {
  const client = supabase
  if (!client) throw new Error('Le foyer n’est pas connecté à un serveur.')

  const { error } = await client.functions.invoke('invite-member', { body: { memberId } })
  if (error) throw new Error(await reasonFrom(error))
}
